import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Save, Plus, Trash2, GripVertical, Type, List, Indent, Outdent, X, Link2, Bold, Italic, Underline, Eraser } from 'lucide-react';
import { SaveCancelBar } from './ui/SaveCancelBar';

// EDITOR INTELIGENTE V2.3 - HIERARQUIA & SUB-ITENS (RESTAURADO)
const ContractEditor = ({ initialData, onSave, isSaving, onCancel, structure, compact = false, hideActions = false, onDraftChange = null }) => {
  const [mode, setMode] = useState('TEXT'); 
  const [items, setItems] = useState([]);
  const [pendingHtml, setPendingHtml] = useState('');
  const [formatState, setFormatState] = useState({ bold: false, italic: false, underline: false });
  const [draggedIndex, setDraggedIndex] = useState(null);

  const editorRef = useRef(null);
  const lastActiveRef = useRef(null); 
  const itemRefs = useRef([]);

  const listStyle = 'NUM';

  useEffect(() => {
    if (!initialData) return;
    if (initialData.type === 'list' && Array.isArray(initialData.items)) {
      setMode('LIST');
      setItems(initialData.items);
    } else {
      setMode('TEXT');
      setItems([]);
      const content =
        initialData.content ||
        (Array.isArray(initialData.items) ? initialData.items.join('<br>') : '') ||
        '';
      setPendingHtml(content);
    }
  }, [initialData]);

  useEffect(() => {
    if (mode !== 'TEXT') return;
    if (!editorRef.current) return;
    // Evita sobrescrever o innerHTML enquanto o usuário está digitando,
    // pois isso reseta a seleção/caret e causa o "pulo" para a esquerda.
    if (document.activeElement === editorRef.current) return;
    const next = pendingHtml || '';
    if (editorRef.current.innerHTML !== next) editorRef.current.innerHTML = next;
    enforceLtr(editorRef.current);
  }, [pendingHtml, mode]);

  const refreshFormatState = () => {
    try {
      setFormatState({
        bold: !!document.queryCommandState('bold'),
        italic: !!document.queryCommandState('italic'),
        underline: !!document.queryCommandState('underline')
      });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const onSelectionChange = () => refreshFormatState();
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  useEffect(() => {
    if (mode !== 'LIST') return;
    for (let i = 0; i < items.length; i += 1) {
      const el = itemRefs.current[i];
      if (!el) continue;
      if (document.activeElement === el) continue;
      const html = stripIndent(items[i]);
      if (el.innerHTML !== html) el.innerHTML = html;
      enforceLtr(el);
    }
  }, [items, mode]);

  const applyFormatCommand = (cmd) => {
    try {
      if (mode === 'TEXT' && editorRef.current) {
        editorRef.current.focus();
        document.execCommand(cmd, false, null);
        setPendingHtml(editorRef.current.innerHTML);
      } else if (mode === 'LIST' && lastActiveRef.current?.el && typeof lastActiveRef.current?.idx === 'number') {
        const idx = lastActiveRef.current.idx;
        const el = lastActiveRef.current.el;
        const currentRaw = items[idx] ?? '';
        const level = getIndentLevel(currentRaw);
        const prefix = level === 2 ? '>>> ' : (level === 1 ? '>> ' : '');

        el.focus();
        document.execCommand(cmd, false, null);

        const nextHtml = normalizeListItemHtml(el.innerHTML);
        handleItemChange(idx, prefix + nextHtml);
      } else {
        document.execCommand(cmd, false, null);
      }

      refreshFormatState();
    } catch {
      // ignore
    }
  };

  const getIndentLevel = (raw) => {
    const t = String(raw || '');
    if (t.startsWith('>>> ')) return 2;
    if (t.startsWith('>> ')) return 1;
    return 0;
  };

  const stripIndent = (raw) => String(raw || '').replace(/^>>>\s|^>>\s/, '');

  const enforceLtr = (el) => {
    if (!el) return;
    try {
      el.setAttribute('dir', 'ltr');
      el.style.direction = 'ltr';
      el.style.unicodeBidi = 'plaintext';

      // Alguns navegadores injetam dir="rtl" em tags internas (ex: <p dir="rtl">).
      // Isso pode voltar a forçar o cursor para a esquerda.
      const rtlNodes = el.querySelectorAll?.('[dir="rtl"], [dir="RTL"], [dir="Rtl"], [dir="rTl"], [style*="direction: rtl"], [style*="direction:rtl"]');
      if (rtlNodes && rtlNodes.length) {
        rtlNodes.forEach((n) => {
          try {
            n.setAttribute('dir', 'ltr');
            if (n.style) {
              n.style.direction = 'ltr';
              n.style.unicodeBidi = 'plaintext';
            }
          } catch {
            // ignore
          }
        });
      }
    } catch {
      // ignore
    }
  };

  const normalizeListItemHtml = (html) => {
    if (!html) return '';
    let out = String(html);
    out = out.replace(/\r?\n/g, '');
    out = out.replace(/\sdir=("|')rtl\1/gi, '');
    out = out.replace(/\sdirection\s*:\s*rtl\s*;?/gi, '');
    out = out.replace(/\sunicode-bidi\s*:\s*[^;"']+\s*;?/gi, '');
    out = out.replace(/<div><br><\/div>/gi, '<br>');
    out = out.replace(/<div>/gi, '<br>');
    out = out.replace(/<\/div>/gi, '');
    out = out.replace(/&nbsp;/g, ' ');
    return out;
  };

  const sanitizeHtmlForContract = (rawHtml) => {
    const html = String(rawHtml || '');
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      if (!root) return '';

      const allowed = new Set(['DIV', 'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI']);

      const walk = (node) => {
        if (!node) return;
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node;

          // remove atributos que trazem fonte/cor/estilo externo
          ['style', 'class', 'color', 'face', 'dir', 'lang'].forEach((a) => el.removeAttribute(a));
          // remove data-* também (cola do Google Docs/Word)
          [...el.attributes].forEach((attr) => {
            if (String(attr.name || '').toLowerCase().startsWith('data-')) el.removeAttribute(attr.name);
          });

          // normaliza tags equivalentes
          if (el.tagName === 'B') {
            const s = doc.createElement('strong');
            s.innerHTML = el.innerHTML;
            el.replaceWith(s);
            walk(s);
            return;
          }
          if (el.tagName === 'I') {
            const em = doc.createElement('em');
            em.innerHTML = el.innerHTML;
            el.replaceWith(em);
            walk(em);
            return;
          }

          // unwrap de tags não permitidas (mantém conteúdo)
          if (!allowed.has(el.tagName)) {
            const frag = doc.createDocumentFragment();
            while (el.firstChild) frag.appendChild(el.firstChild);
            el.replaceWith(frag);
            return;
          }
        }

        let child = node.firstChild;
        while (child) {
          const next = child.nextSibling;
          walk(child);
          child = next;
        }
      };

      walk(root);
      return root.innerHTML;
    } catch {
      // fallback conservador
      return html
        .replace(/\sstyle=("|')[\s\S]*?\1/gi, '')
        .replace(/\sclass=("|')[\s\S]*?\1/gi, '')
        .replace(/\sdir=("|')[\s\S]*?\1/gi, '');
    }
  };

  const handleInsertVar = (code) => {
    if (mode === 'TEXT' && editorRef.current) {
        editorRef.current.focus();
        document.execCommand('insertText', false, code);
    } else if (lastActiveRef.current?.el && typeof lastActiveRef.current?.idx === 'number') {
        const idx = lastActiveRef.current.idx;
        const el = lastActiveRef.current.el;
        const currentRaw = items[idx] ?? '';
        const level = getIndentLevel(currentRaw);
        const prefix = level === 2 ? '>>> ' : (level === 1 ? '>> ' : '');

        el.focus();
        document.execCommand('insertText', false, code);

        const nextHtml = normalizeListItemHtml(el.innerHTML);
        handleItemChange(idx, prefix + nextHtml);
    } else {
        navigator.clipboard.writeText(code);
        alert(`Variável ${code} copiada!`);
    }
  };

  const handleItemChange = (idx, val) => {
    const newItems = [...items];
    newItems[idx] = val;
    setItems(newItems);
    if (typeof onDraftChange === 'function') {
      try {
        onDraftChange({ type: 'list', items: newItems, content: null, listStyle });
      } catch {
        // ignore
      }
    }
  };

  const addItem = () => setItems([...items, '']);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const insertItemAfter = (idx) => {
    const currentRaw = items[idx] ?? '';
    const level = getIndentLevel(currentRaw);
    const prefix = level === 2 ? '>>> ' : (level === 1 ? '>> ' : '');

    const next = [...items];
    next.splice(idx + 1, 0, prefix);
    setItems(next);

    setTimeout(() => {
      const el = itemRefs.current[idx + 1];
      if (el) {
        el.focus();
        lastActiveRef.current = { idx: idx + 1, el };
      }
    }, 0);
  };

  const handleDragStart = (e, idx) => {
    if (typeof idx !== 'number') return;
    setDraggedIndex(idx);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
    } catch {
      // ignore
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = 'move';
    } catch {
      // ignore
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const from = (() => {
      if (typeof draggedIndex === 'number') return draggedIndex;
      try {
        const raw = e.dataTransfer.getData('text/plain');
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
      } catch {
        return null;
      }
    })();

    if (typeof from !== 'number' || typeof dropIndex !== 'number') {
      setDraggedIndex(null);
      return;
    }
    if (from === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    if (from < 0 || from >= items.length || dropIndex < 0 || dropIndex >= items.length) {
      setDraggedIndex(null);
      return;
    }

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    setItems(next);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => setDraggedIndex(null);

  // --- LÓGICA DE SUB-CLÁUSULAS (Simulação Visual com CSS) ---
  const indentItem = (idx) => {
      const current = items[idx];
      const level = getIndentLevel(current);
      if (level === 0) handleItemChange(idx, '>> ' + stripIndent(current));
      if (level === 1) handleItemChange(idx, '>>> ' + stripIndent(current));
  };
  const outdentItem = (idx) => {
      const current = items[idx];
      const level = getIndentLevel(current);
      if (level === 1) handleItemChange(idx, stripIndent(current));
      if (level === 2) handleItemChange(idx, '>> ' + stripIndent(current));
  };

  const getListMarkers = () => {
    const markers = [];
    let main = 0;
    let sub = 0;
    let subsub = 0;

    for (let i = 0; i < items.length; i++) {
      const level = getIndentLevel(items[i]);
      if (level === 0) {
        main += 1;
        sub = 0;
        subsub = 0;
        markers.push(`${main}.`);
      } else if (level === 1) {
        sub += 1;
        subsub = 0;
        markers.push(`${main}.${sub}.`);
      } else {
        subsub += 1;
        markers.push(`${main}.${sub}.${subsub}.`);
      }
    }
    return markers;
  };

  const clauseOptions = useMemo(() => {
    const cleanClauseTitle = (title) => {
      const t = String(title || '').trim();
      return t.replace(/^\d+(?:\.\d+)*\s*[-–—]\s*/g, '');
    };

    const isPreambuloBlock = (block) => {
      return block?.id === 'preambulo' || String(block?.title || '').toUpperCase().includes('PREÂMBULO');
    };

    const isOptionLikeBlock = (block) => {
      const id = String(block?.id || '');
      const t = String(block?.title || '').toLowerCase();
      if (/_opt_/.test(id)) return true;
      if (t.includes('opção')) return true;
      if (
        t.includes('pessoa física') ||
        t.includes('pessoa jurid') ||
        t.includes('incapaz') ||
        t.includes('menor') ||
        t.includes('espólio') ||
        t.includes('condom') ||
        t.includes('animal') ||
        t.includes('privada de liberdade')
      ) return true;
      return false;
    };

    const isNavigableClause = (block) => {
      if (!block) return false;
      if (String(block?.id || '').startsWith('cl_01_')) return false;
      if (block.isSubClause) return false;
      if (isPreambuloBlock(block)) return false;
      if (isOptionLikeBlock(block)) return false;
      return true;
    };

    const baseBlocks = Array.isArray(structure) ? structure.filter((b) => isNavigableClause(b)) : [];
    const hasRealDasPartes = baseBlocks.some((b) => String(b?.id || '') === '__das_partes');
    const syntheticDasPartes = { id: '__das_partes', title: '1 - DAS PARTES', type: 'text' };
    const mapBlocks = hasRealDasPartes ? baseBlocks : [syntheticDasPartes, ...baseBlocks];

    return mapBlocks.map((b) => ({
      id: b.id,
      title: cleanClauseTitle(b.title)
    }));
  }, [structure]);

  const handleSaveClick = () => {
    if (mode === 'LIST') {
      const validItems = items.filter(i => {
        const body = stripIndent(i);
        const plain = body.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        return plain !== '';
      });
      const sanitizedItems = validItems.map((i) => {
        const level = getIndentLevel(i);
        const prefix = level === 2 ? '>>> ' : (level === 1 ? '>> ' : '');
        const body = stripIndent(i);
        const cleaned = sanitizeHtmlForContract(normalizeListItemHtml(body));
        return prefix + cleaned;
      });
      onSave({ type: 'list', items: sanitizedItems, content: null, listStyle });
    } else {
      const html = editorRef.current ? editorRef.current.innerHTML : '';
      const cleaned = sanitizeHtmlForContract(normalizeListItemHtml(html));
      onSave({ type: 'text', content: cleaned, items: null, listStyle });
    }
  };

  return (
    <div className={`flex flex-col bg-white rounded-lg overflow-hidden h-full ${compact ? 'min-h-[240px]' : 'min-h-[450px]'}`}>
      <div className="flex flex-col border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold text-gray-400 mr-2">Modo:</span>
                <button onClick={() => setMode('TEXT')} className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-bold ${mode === 'TEXT' ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}><Type className="w-3 h-3"/> Texto</button>
                <button onClick={() => { if (mode === 'TEXT') { setItems(editorRef.current?.innerText ? [editorRef.current.innerText] : ['']); setMode('LIST'); } }} className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-bold ${mode === 'LIST' ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}><List className="w-3 h-3"/> Lista</button>
                <div className="flex items-center gap-1 ml-0 md:ml-2 bg-white border border-gray-200 rounded p-0.5">
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); applyFormatCommand('bold'); }} className={`px-2 py-1 rounded text-[10px] font-bold ${formatState.bold ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`} title="Negrito"><Bold className="w-3 h-3"/></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); applyFormatCommand('italic'); }} className={`px-2 py-1 rounded text-[10px] font-bold ${formatState.italic ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`} title="Itálico"><Italic className="w-3 h-3"/></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); applyFormatCommand('underline'); }} className={`px-2 py-1 rounded text-[10px] font-bold ${formatState.underline ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`} title="Sublinhado"><Underline className="w-3 h-3"/></button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); applyFormatCommand('removeFormat'); }} className="px-2 py-1 rounded text-[10px] font-bold text-gray-600 hover:bg-gray-100" title="Limpar formatação"><Eraser className="w-3 h-3"/></button>
                </div>
                {clauseOptions.length > 0 && (
                  <div className="flex items-center gap-2 ml-0 md:ml-2">
                    <span className="text-[9px] font-bold text-yellow-700 whitespace-nowrap">Ref.:</span>
                    <select className="px-2 py-1 bg-white border border-yellow-200 rounded text-[10px] font-bold text-gray-700 max-w-[220px]" defaultValue="" onChange={(e) => { const val = e.target.value; if (!val) return; handleInsertVar(`[[REF_CLAUSE:${val}]]`); e.target.value = ''; }}>
                      <option value="">Inserir cláusula...</option>
                      {clauseOptions.map((c) => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                    <button onClick={() => handleInsertVar('[[REF_CLAUSE:SELECIONE_NO_MAPA]]')} className="px-2 py-1 bg-white border border-yellow-200 rounded text-[10px] font-bold text-gray-600 hover:bg-yellow-100 whitespace-nowrap" title="Placeholder de referência"><Link2 className="w-3 h-3 inline"/></button>
                  </div>
                )}
              </div>
            {!hideActions ? (
              typeof onCancel === 'function' ? (
                <SaveCancelBar
                  onCancel={onCancel}
                  onSave={handleSaveClick}
                  cancelLabel="Cancelar"
                  saveLabel="Salvar"
                  saving={isSaving}
                />
              ) : (
                <button onClick={handleSaveClick} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 rounded text-xs font-bold text-white bg-green-600 hover:bg-green-700 shadow-md"><Save className="w-4 h-4"/> SALVAR</button>
              )
            ) : null}
          </div>
      </div>

      <div className={`flex-1 bg-white overflow-y-auto ${compact ? 'p-3' : 'p-6'}`}>
        {mode === 'LIST' ? (
          <div className="space-y-3">
            {(() => {
              const markers = getListMarkers();
              return items.map((item, idx) => {
                const level = getIndentLevel(item);
                const isSub = level > 0;
                const isSubSub = level > 1;
                const marginClass = isSubSub ? 'ml-14' : (isSub ? 'ml-8' : '');
                const borderClass = isSub ? 'pl-4 border-l-2 border-blue-200' : '';
                return (
                  <div
                    key={idx}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, idx)}
                    className={`flex items-start gap-2 group animate-fade-in-up ${marginClass} ${borderClass} ${draggedIndex === idx ? 'opacity-60' : ''}`}
                  >
                    <div className="flex flex-col gap-1 mt-2 opacity-40 group-hover:opacity-100">
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragEnd={handleDragEnd}
                        className="text-gray-300 cursor-grab hover:text-gray-500"
                        title="Arraste para reordenar"
                      >
                        <GripVertical className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 mt-2 opacity-30 group-hover:opacity-100 border-r border-gray-200 pr-2 mr-1">
                        <button onClick={() => indentItem(idx)} title="Recuar (Sub-item)" disabled={level >= 2}><Indent className="w-3 h-3 hover:text-purple-600"/></button>
                        <button onClick={() => outdentItem(idx)} title="Avançar (Item Principal)" disabled={level === 0}><Outdent className="w-3 h-3 hover:text-purple-600"/></button>
                    </div>
                    <div className="mt-2 text-xs font-bold text-gray-400 w-16 text-right select-none">{markers[idx]}</div>
                    <div className="flex-1">
                        <div
                            role="textbox"
                            contentEditable
                            dir="ltr"
                            style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}
                            suppressContentEditableWarning={true}
                            ref={(el) => { itemRefs.current[idx] = el; }}
                            onFocus={(e) => {
                              enforceLtr(e.currentTarget);
                              lastActiveRef.current = { idx, el: e.currentTarget };
                            }}
                            onInput={(e) => {
                              enforceLtr(e.currentTarget);
                              const nextHtml = normalizeListItemHtml(e.currentTarget.innerHTML);
                              if (level === 2) handleItemChange(idx, '>>> ' + nextHtml);
                              else if (level === 1) handleItemChange(idx, '>> ' + nextHtml);
                              else handleItemChange(idx, nextHtml);
                            }}
                            onKeyUp={(e) => enforceLtr(e.currentTarget)}
                            onPaste={(e) => { setTimeout(() => enforceLtr(e.currentTarget), 0); }}
                            className="w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none min-h-[60px]"
                        />
                    </div>
                    <div className="mt-2 flex flex-col gap-2">
                      <button onClick={() => insertItemAfter(idx)} className="text-gray-300 hover:text-blue-600" title="Inserir item abaixo" type="button">
                        <Plus className="w-4 h-4"/>
                      </button>
                      <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500" title="Remover item" type="button">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
            <button onClick={addItem} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:text-blue-600 flex justify-center gap-2 text-xs font-bold mt-4"><Plus className="w-4 h-4"/> Adicionar Item</button>
          </div>
        ) : (
          <div className="h-full min-h-[300px] border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-500">
              <div
                ref={editorRef}
                contentEditable
                dir="ltr"
                style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}
                className="w-full h-full p-6 text-gray-800 font-serif text-base outline-none overflow-y-auto"
                suppressContentEditableWarning={true}
                onFocus={(e) => enforceLtr(e.currentTarget)}
                onInput={(e) => {
                  enforceLtr(e.currentTarget);
                  const next = e.currentTarget.innerHTML;
                  setPendingHtml(next);
                  if (typeof onDraftChange === 'function') {
                    try {
                      const cleaned = sanitizeHtmlForContract(normalizeListItemHtml(next));
                      onDraftChange({ type: 'text', content: cleaned, items: null, listStyle });
                    } catch {
                      // ignore
                    }
                  }
                }}
                onKeyUp={(e) => { enforceLtr(e.currentTarget); refreshFormatState(); }}
                onMouseUp={() => refreshFormatState()}
                onPaste={(e) => { setTimeout(() => enforceLtr(e.currentTarget), 0); }}
              />

          </div>
        )}
      </div>
    </div>
  );
};
export default ContractEditor;